import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Field, PageHeader, Skeleton, Spinner, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

interface Company {
  id: string;
  slug: string;
  nameAr: string;
  nameEn: string | null;
  timezone: string;
  locale: 'AR' | 'EN';
  currency: string;
  settings: {
    workingHours: { days: number[]; from: string; to: string };
    speedLimits: { defaultKph: number };
    offlineMinutes: number;
    idleMinutes: number;
    stopMinutes: number;
    vatRatePct: number;
    cargoTypes: string[];
  };
}

interface FormValues {
  nameAr: string;
  nameEn: string;
  timezone: string;
  locale: 'AR' | 'EN';
  from: string;
  to: string;
  days: number[];
  defaultKph: number;
  offlineMinutes: number;
  idleMinutes: number;
  stopMinutes: number;
  vatRatePct: number;
  cargoTypes: string;
}

const DAYS = [0, 1, 2, 3, 4, 5, 6];

export function CompanySettingsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const reloadMe = useSession((s) => s.reloadMe);
  const q = useQuery({ queryKey: ['settings'], queryFn: () => api<Company>('/settings') });
  const form = useForm<FormValues>();

  useEffect(() => {
    if (!q.data) return;
    const c = q.data;
    form.reset({
      nameAr: c.nameAr,
      nameEn: c.nameEn ?? '',
      timezone: c.timezone,
      locale: c.locale,
      from: c.settings.workingHours.from,
      to: c.settings.workingHours.to,
      days: c.settings.workingHours.days,
      defaultKph: c.settings.speedLimits.defaultKph,
      offlineMinutes: c.settings.offlineMinutes,
      idleMinutes: c.settings.idleMinutes,
      stopMinutes: c.settings.stopMinutes,
      vatRatePct: c.settings.vatRatePct,
      cargoTypes: c.settings.cargoTypes.join('، '),
    });
  }, [q.data, form]);

  const save = useMutation({
    mutationFn: async (v: FormValues) => {
      await api('/settings/company', { method: 'PATCH', json: { nameAr: v.nameAr, nameEn: v.nameEn || null, timezone: v.timezone, locale: v.locale } });
      await api('/settings', {
        method: 'PATCH',
        json: {
          workingHours: { days: v.days.map(Number), from: v.from, to: v.to },
          speedLimits: { defaultKph: Number(v.defaultKph), byType: {} },
          offlineMinutes: Number(v.offlineMinutes),
          idleMinutes: Number(v.idleMinutes),
          stopMinutes: Number(v.stopMinutes),
          vatRatePct: Number(v.vatRatePct),
          cargoTypes: v.cargoTypes.split(/[،,]/).map((s) => s.trim()).filter(Boolean),
        },
      });
    },
    onSuccess: async () => {
      toast.success(t('common.saved'));
      await qc.invalidateQueries({ queryKey: ['settings'] });
      await reloadMe();
    },
    onError: toast.error,
  });

  if (q.isLoading) return <Skeleton className="h-96" />;
  if (q.error) return <p className="text-danger-fg">{t('common.error')}</p>;
  const readOnly = !can('settings:update');

  return (
    <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="flex flex-col gap-5">
      <PageHeader
        title={t('settings.company.title')}
        subtitle={`${t('settings.company.slug')}: ${q.data?.slug}`}
        actions={
          readOnly ? null : (
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? <Spinner /> : null}
              {t('common.save')}
            </button>
          )
        }
      />

      <fieldset disabled={readOnly} className="card grid gap-4 p-5 md:grid-cols-2">
        <legend className="sr-only">{t('settings.company.profile')}</legend>
        <Field label={t('settings.company.nameAr')} required>
          {(id) => <input id={id} className="input" {...form.register('nameAr', { required: true })} />}
        </Field>
        <Field label={t('settings.company.nameEn')}>
          {(id) => <input id={id} className="input" dir="ltr" {...form.register('nameEn')} />}
        </Field>
        <Field label={t('settings.company.timezone')}>
          {(id) => <input id={id} className="input" dir="ltr" {...form.register('timezone')} />}
        </Field>
        <Field label={t('settings.company.locale')}>
          {(id) => (
            <select id={id} className="input" {...form.register('locale')}>
              <option value="AR">العربية</option>
              <option value="EN">English</option>
            </select>
          )}
        </Field>
      </fieldset>

      <fieldset disabled={readOnly} className="card grid gap-4 p-5 md:grid-cols-3">
        <h3 className="col-span-full text-sm font-bold">{t('settings.company.workingHours')}</h3>
        <Field label={t('settings.company.from')}>{(id) => <input id={id} type="time" className="input" dir="ltr" {...form.register('from')} />}</Field>
        <Field label={t('settings.company.to')}>{(id) => <input id={id} type="time" className="input" dir="ltr" {...form.register('to')} />}</Field>
        <div>
          <span className="label">{t('settings.company.days')}</span>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d) => (
              <label key={d} className="flex min-h-9 cursor-pointer items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 text-xs has-[:checked]:border-brand-ink has-[:checked]:bg-brand-soft">
                <input type="checkbox" value={d} className="accent-brand" {...form.register('days')} />
                {t(`days.${d}`)}
              </label>
            ))}
          </div>
        </div>
      </fieldset>

      <fieldset disabled={readOnly} className="card grid gap-4 p-5 md:grid-cols-3">
        <h3 className="col-span-full text-sm font-bold">{t('settings.company.tracking')}</h3>
        <Field label={t('settings.company.defaultKph')}>{(id) => <input id={id} type="number" className="input" dir="ltr" {...form.register('defaultKph')} />}</Field>
        <Field label={t('settings.company.offlineMinutes')}>{(id) => <input id={id} type="number" className="input" dir="ltr" {...form.register('offlineMinutes')} />}</Field>
        <Field label={t('settings.company.idleMinutes')}>{(id) => <input id={id} type="number" className="input" dir="ltr" {...form.register('idleMinutes')} />}</Field>
        <Field label={t('settings.company.stopMinutes')}>{(id) => <input id={id} type="number" className="input" dir="ltr" {...form.register('stopMinutes')} />}</Field>
        <Field label={t('settings.company.vatRatePct')}>{(id) => <input id={id} type="number" step="0.5" className="input" dir="ltr" {...form.register('vatRatePct')} />}</Field>
        <Field label={t('settings.company.cargoTypes')} hint={t('settings.company.cargoTypesHint')}>
          {(id) => <input id={id} className="input" {...form.register('cargoTypes')} />}
        </Field>
      </fieldset>
    </form>
  );
}
